import { useThreadComposerLayout } from "./threadComposerLayoutContext";
import { pinRestingComposerOverlay } from "./restingComposerOverlayPin";
import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";

import { shouldAnimateComposerRestingTransition } from "../composerFooterLayout";
import {
  canAnimate,
  readComposerContentGeometry,
  type ComposerContentGeometry,
  isHeightChangeWorthAnimating,
  prefersReducedMotion,
  startRestingComposerTransition,
  type RestingComposerTransitionAnimations,
  RESTING_TRANSITION_CLEANUP_BUFFER_MS,
  RESTING_TRANSITION_DURATION_MS,
} from "./restingComposerTransitionMotion";

/** Animate the main surface while its context strip and timeline reservation stay fixed. */
export function useRestingComposerTransition(input: {
  readonly formRef: RefObject<HTMLFormElement | null>;
  readonly controlsRef: RefObject<HTMLDivElement | null>;
  readonly isResting: boolean;
}): void {
  const { formRef, controlsRef, isResting } = input;
  const { publishOverlayHeight } = useThreadComposerLayout();
  const releaseOverlayRef = useRef<(() => void) | null>(null);
  const geometryRef = useRef<ComposerContentGeometry | null>(null);
  const previousRestingRef = useRef(isResting);
  const previousHeightRef = useRef<number | null>(null);
  const animationsRef = useRef<RestingComposerTransitionAnimations | null>(null);
  const cleanupTimeoutRef = useRef<number | null>(null);
  const hasCompletedInitialLayoutRef = useRef(false);

  const cancelPendingCleanup = useCallback(() => {
    if (cleanupTimeoutRef.current === null) return;
    window.clearTimeout(cleanupTimeoutRef.current);
    cleanupTimeoutRef.current = null;
  }, []);

  /**
   * Stop whatever is running and hand back the height it had reached.
   *
   * Retargeting from the visible height is what makes a change of mind
   * mid-tween continue from where it is rather than jump back to the start.
   */
  const interrupt = useCallback((): number | null => {
    const animations = animationsRef.current;
    const root = formRef.current;
    const form = root?.querySelector<HTMLElement>('[data-chat-composer-surface="true"]') ?? root;
    animationsRef.current = null;
    // Pinned for the tween only, so later attachment, font and viewport changes
    // stay natural.
    form?.style.removeProperty("overflow");
    releaseOverlayRef.current?.();
    releaseOverlayRef.current = null;
    if (!animations || !form) return null;
    const reachedHeight = form.getBoundingClientRect().height;
    geometryRef.current = readComposerContentGeometry(form);
    animations.height.cancel();
    for (const animation of animations.content) animation.cancel();
    animations.arrival?.cancel();
    return reachedHeight;
  }, [formRef]);

  useLayoutEffect(() => {
    const root = formRef.current;
    const form = root?.querySelector<HTMLElement>('[data-chat-composer-surface="true"]') ?? root;
    const stateChanged = previousRestingRef.current !== isResting;
    previousRestingRef.current = isResting;
    if (!form) return;

    cancelPendingCleanup();
    const interruptedHeight = interrupt();
    const nextHeight = form.getBoundingClientRect().height;
    const overlay = form.closest<HTMLElement>('[data-chat-composer-overlay="true"]');
    if (overlay) publishOverlayHeight(overlay.getBoundingClientRect().height);
    const previousHeight = interruptedHeight ?? previousHeightRef.current;
    const previousGeometry = geometryRef.current;
    geometryRef.current = readComposerContentGeometry(form);
    previousHeightRef.current = nextHeight;

    const wanted = shouldAnimateComposerRestingTransition({
      hasCompletedInitialLayout: hasCompletedInitialLayoutRef.current,
      stateChanged,
      hasInterruptedAnimation: interruptedHeight !== null,
    });
    hasCompletedInitialLayoutRef.current = true;
    if (!wanted || !canAnimate(form) || prefersReducedMotion()) return;
    if (!isHeightChangeWorthAnimating(previousHeight, nextHeight)) return;

    releaseOverlayRef.current = pinRestingComposerOverlay(form, isResting);
    form.style.overflow = "clip";
    const animations = startRestingComposerTransition({
      form,
      controls: controlsRef.current,
      previousHeight,
      nextHeight,
      isResting,
      stateChanged,
      previousGeometry,
    });
    animationsRef.current = animations;

    const finish = () => {
      if (animationsRef.current !== animations) return;
      cancelPendingCleanup();
      animationsRef.current = null;
      animations.height.cancel();
      animations.arrival?.cancel();
      for (const animation of animations.content) animation.cancel();
      form.style.removeProperty("overflow");
      releaseOverlayRef.current?.();
      releaseOverlayRef.current = null;
    };
    void animations.height.finished.catch(() => undefined).then(finish);
    cleanupTimeoutRef.current = window.setTimeout(
      finish,
      RESTING_TRANSITION_DURATION_MS + RESTING_TRANSITION_CLEANUP_BUFFER_MS,
    );
  }, [cancelPendingCleanup, controlsRef, formRef, interrupt, isResting, publishOverlayHeight]);

  useLayoutEffect(() => {
    const form = formRef.current;
    const surface = form?.querySelector<HTMLElement>('[data-chat-composer-surface="true"]');
    if (!surface) return;
    const observer = new ResizeObserver(() => {
      if (!animationsRef.current) {
        previousHeightRef.current = surface.getBoundingClientRect().height;
        geometryRef.current = readComposerContentGeometry(surface);
      }
    });
    observer.observe(surface);
    return () => observer.disconnect();
  }, [formRef]);

  useLayoutEffect(
    () => () => {
      cancelPendingCleanup();
      interrupt();
    },
    [cancelPendingCleanup, interrupt],
  );
}
